import useSuitesProjectsLanguage from '../useSuitesProjectsLanguage';
import React from 'react';
import { Workflow, FlaskConical, Settings2, Monitor } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import './suitesTheme.css';

export default function SuiteCreateDialog({
  showCreateModal,
  setShowCreateModal,
  formData,
  setFormData,
  flowConfig,
  setFlowConfig,
  availableEnvironments,
  handleCreateSuite
}) {
  const { t } = useSuitesProjectsLanguage();
  return (<>
      {/* Create Suite Modal */}
      {showCreateModal && (
        <div className="lexa-suite-overlay fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="lexa-suite-dialog lexa-suite-dialog--create w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">{t("Create Test Suite")}</h2>

            <div className="space-y-4">
              {/* Suite Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("Suite Type")}</label>
                <div className="lexa-suite-config-grid grid grid-cols-2 gap-3">
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.suite_type === 'flow_testing'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="suite_type"
                      value="flow_testing"
                      checked={formData.suite_type === 'flow_testing'}
                      onChange={(e) => setFormData({ ...formData, suite_type: e.target.value })}
                      className="sr-only"
                    />
                    <Workflow className="w-5 h-5 mr-2 text-indigo-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t("Flow Testing")}</p>
                      <p className="text-xs text-gray-500">{t("Browser automation")}</p>
                    </div>
                  </label>
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.suite_type === 'api_testing'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="suite_type"
                      value="api_testing"
                      checked={formData.suite_type === 'api_testing'}
                      onChange={(e) => setFormData({ ...formData, suite_type: e.target.value })}
                      className="sr-only"
                    />
                    <FlaskConical className="w-5 h-5 mr-2 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t("API Testing")}</p>
                      <p className="text-xs text-gray-500">{t("REST API tests")}</p>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Name")}</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t("e.g., Smoke Tests, Regression Suite")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Description")}</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t("What does this suite test?")}
                />
              </div>

              {/* Flow Testing Configuration */}
              {formData.suite_type === 'flow_testing' && (
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <div className="flex items-center text-sm font-medium text-gray-700">
                    <Settings2 className="w-4 h-4 mr-2" />
                    {t("Execution Configuration")}
                  </div>

                  <div className="lexa-suite-config-grid grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t("Browser")}</label>
                      <select
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                        value={flowConfig.browser}
                        onChange={(e) => setFlowConfig({ ...flowConfig, browser: e.target.value })}
                      >
                        <option value="chromium">Chrome</option>
                        <option value="firefox">Firefox</option>
                        <option value="webkit">Safari</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t("Device Type")}</label>
                      <select
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                        value={flowConfig.device_type}
                        onChange={(e) => setFlowConfig({ ...flowConfig, device_type: e.target.value })}
                      >
                        <option value="desktop">{t("Desktop")}</option>
                        <option value="mobile">{t("Mobile")}</option>
                        <option value="tablet">{t("Tablet")}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t("Environment")}</label>
                    <select
                      className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                      value={flowConfig.environment_id || ''}
                      onChange={(e) => setFlowConfig({ ...flowConfig, environment_id: e.target.value || null })}
                    >
                      <option value="">{t("Use test's default environment")}</option>
                      {availableEnvironments.map(env => (
                        <option key={env.id} value={env.id}>{env.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center justify-between p-2 bg-gray-100 rounded">
                    <div className="flex items-center text-sm text-gray-600">
                      <Monitor className="w-4 h-4 mr-2" />
                      {t("Headless Mode")}
                    </div>
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">{t("Always On")}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("When a test fails")}</label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="execution_mode"
                      value="continue_on_failure"
                      checked={formData.execution_mode === 'continue_on_failure'}
                      onChange={(e) => setFormData({ ...formData, execution_mode: e.target.value })}
                      className="mr-3"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t("Continue running")}</p>
                      <p className="text-xs text-gray-500">{t("Run all tests even if some fail")}</p>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="execution_mode"
                      value="stop_on_failure"
                      checked={formData.execution_mode === 'stop_on_failure'}
                      onChange={(e) => setFormData({ ...formData, execution_mode: e.target.value })}
                      className="mr-3"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t("Stop immediately")}</p>
                      <p className="text-xs text-gray-500">{t("Stop the suite on first failure")}</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => {
                setShowCreateModal(false);
                setFormData({ name: '', description: '', execution_mode: 'continue_on_failure', suite_type: 'flow_testing' });
              }}>
                {t("Cancel")}
              </Button>
              <Button className="lexa-suite-primary-action" onClick={handleCreateSuite} disabled={!formData.name.trim()}>
                {t("Create Suite")}
              </Button>
            </div>
          </Card>
        </div>
      )}
  </>);
}
